import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { removeAuthCookies } from "@/lib/utils";

export interface AuthContextType {
  isAuthenticated: boolean;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const useAuth = (): AuthContextType => {
  const { isAuthenticated, loginWithRedirect, logout: auth0Logout } = useAuth0();

  const login = useCallback(
    async (returnTo?: string) => {
      await loginWithRedirect({ appState: { returnTo: returnTo || "/" } });
    },
    [loginWithRedirect]
  );

  const logout = useCallback(async () => {
    removeAuthCookies();
    // Mark this as an explicit, user-initiated sign-out. When Auth0 redirects
    // back to the app, SignInPage reads this flag and shows the interactive
    // sign-in screen instead of auto-firing loginWithRedirect(). Without it,
    // the launcher's silent-SSO redirect re-authenticates against any lingering
    // or shared (superapp) SSO session and signs the user straight back in —
    // making "Sign out" look like a no-op ("reloads to the same creds").
    localStorage.setItem("ts_explicit_logout", "1");
    await auth0Logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }, [auth0Logout]);

  return { isAuthenticated, login, logout };
};
